// The CRM's own automations (docs/specs/automations.md): switch each on or
// off, keep it in dry run until its log looks right, pause everything at once.
// Messages to leads run in GoHighLevel; these do the internal work.

import { useState } from 'react';
import {
  saveOffer,
  setAutomation,
  setKillSwitch,
  useAutomations,
  useOffers,
  type Automation,
  type Offer,
} from '../../lib/data';
import { ErrorBox, useDrawer, useToast } from '../../ui/kit';
import { Icon } from '../../ui/icons';

export function Automations() {
  const q = useAutomations();
  const toast = useToast();
  const { open } = useDrawer();
  const d = q.data;

  async function run(p: Promise<unknown>, done: string) {
    try {
      await p;
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Automations</span>
          </h1>
          <p>
            Tasks, next steps, reminders and new clients, done for you. Messages to leads stay in
            GoHighLevel; these start them with a tag.
          </p>
        </div>
      </div>
      <ErrorBox error={q.error} />
      {!d ? (
        <section className="card sec loading">Loading…</section>
      ) : (
        <>
          <section className="card kill">
            <span className={`sdot ${d.kill ? 'err' : 'ok'}`} aria-hidden />
            <div className="grow">
              <b>{d.kill ? 'All automations are paused' : 'Automations are running'}</b>
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                Dry run shows what a step would do and does nothing. Turn it off per automation once
                its log looks right.
              </div>
            </div>
            <button
              className={`btn ${d.kill ? 'primary' : ''}`}
              onClick={() =>
                void run(setKillSwitch(!d.kill), d.kill ? 'Automations resumed' : 'All paused')
              }
            >
              {d.kill ? 'Resume all' : 'Pause all'}
            </button>
          </section>
          <div className="autos">
            {d.autos.map((a) => (
              <Card
                key={a.id}
                a={a}
                inside={d.enrolled.filter((e) => e.automation_id === a.id).length}
                done={
                  d.runs.filter(
                    (r) => r.automation_id === a.id && (r.status === 'sent' || r.result?.dry_run),
                  ).length
                }
                waiting={
                  d.runs.filter((r) => r.automation_id === a.id && r.status === 'pending').length
                }
                onToggle={() =>
                  void run(
                    setAutomation(a.key, !a.enabled, a.dry_run),
                    `${a.name} ${a.enabled ? 'off' : 'on'}`,
                  )
                }
                onOpen={() => open({ type: 'auto', key: a.key })}
              />
            ))}
          </div>
          <Offers />
        </>
      )}
    </>
  );
}

// One WhatsApp template per offer: the lead's campaign name picks the offer, the
// offer's tag starts that offer's workflow in GHL (docs/specs/automations.md).
function Offers() {
  const q = useOffers();
  const toast = useToast();
  const blank: Offer = { key: '', name: '', match: '', wa_tag: '', active: true };
  const [form, setForm] = useState<Offer>(blank);

  async function save(o: Offer) {
    try {
      await saveOffer(o);
      setForm(blank);
      toast('Offer saved');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section className="card sec" style={{ marginTop: 24 }} data-testid="offers">
      <div className="chead">
        <span className="lbl p">Offers and their WhatsApp template</span>
      </div>
      <p className="sub">
        A new lead gets the WhatsApp template of its offer. The offer is found from the campaign
        name (use % as a wildcard). In GHL, a workflow started by the tag sends the approved
        template. Leads of the Facebook form are messaged by GHL itself.
      </p>
      <div className="scroll" tabIndex={0} role="region" aria-label="Offers table">
        <table className="tbl">
          <thead>
            <tr>
              <th>Offer</th>
              <th>Campaign name contains</th>
              <th>GHL tag</th>
              <th>On</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((o) => (
              <tr key={o.key}>
                <td>
                  <b>{o.name}</b>
                </td>
                <td>
                  <code>{o.match}</code>
                </td>
                <td>
                  <code>{o.wa_tag}</code>
                </td>
                <td>
                  <button
                    className="sw"
                    role="switch"
                    aria-checked={o.active}
                    aria-label={`Turn ${o.name} on or off`}
                    onClick={() => void save({ ...o, active: !o.active })}
                  />
                </td>
                <td>
                  <button className="btn sm" onClick={() => setForm(o)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form
        className="grid2"
        style={{ padding: 18 }}
        onSubmit={(e) => {
          e.preventDefault();
          void save(form);
        }}
      >
        <label className="field">
          <span>Name</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>
        <label className="field">
          <span>Key (letters, digits, _)</span>
          <input
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })}
            required
          />
        </label>
        <label className="field">
          <span>Campaign name contains (%)</span>
          <input
            value={form.match}
            onChange={(e) => setForm({ ...form, match: e.target.value })}
            required
          />
        </label>
        <label className="field">
          <span>GHL tag</span>
          <input
            value={form.wa_tag}
            onChange={(e) => setForm({ ...form, wa_tag: e.target.value.toLowerCase() })}
            required
          />
        </label>
        <div>
          <button className="btn primary" type="submit">
            {q.data?.some((o) => o.key === form.key) ? 'Save changes' : 'Add offer'}
          </button>
        </div>
      </form>
    </section>
  );
}

function Card({
  a,
  inside,
  done,
  waiting,
  onToggle,
  onOpen,
}: {
  a: Automation;
  inside: number;
  done: number;
  waiting: number;
  onToggle: () => void;
  onOpen: () => void;
}) {
  return (
    <section className="card auto">
      <div className="top">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>
            {a.name} {a.enabled && a.dry_run && <span className="pill sm or">Dry run</span>}
          </h3>
          <div className="trig">When: {a.trigger_text}</div>
        </div>
        <button
          className="sw"
          role="switch"
          aria-checked={a.enabled}
          aria-label={`Turn ${a.name} on or off`}
          onClick={onToggle}
        />
      </div>
      <div className="steps">
        {a.step_labels.slice(0, 4).map((s) => (
          <span key={s} className="pill sm gr">
            {s}
          </span>
        ))}
      </div>
      {a.error_text && <div className="err-note">{a.error_text}</div>}
      <div className="stats3">
        <div>
          <b>{inside}</b>
          <span>leads inside</span>
        </div>
        <div>
          <b>{done}</b>
          <span>{a.dry_run ? 'would do, 7 days' : 'done, 7 days'}</span>
        </div>
        <div>
          <b>{waiting}</b>
          <span>steps waiting</span>
        </div>
      </div>
      <button className="viewall" style={{ marginLeft: 0 }} onClick={onOpen}>
        Leads, steps and runs <Icon name="arrow" />
      </button>
    </section>
  );
}
